/* eslint-disable immutable/no-mutation */
import React, {
  useEffect, useState, useRef, useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import { useParams } from 'react-router-dom';
import { useGetProductsQuery } from '../../services/products';
import { Pagination } from '../Pagination/Pagination';
import { ProductItem } from '../ProductItem/ProductItem';
import { NoSearchResultFound } from '../NoSearchResultFound/NoSearchResultFound';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { setParsedFilters, setAppliedFilters } from '../../features/products/productsSlice';
import './ProductList.scss';
import { Container } from '../Layout/Container';
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';

const PRODUCTS_PER_PAGE = 16;

export const ProductsList = () => {
  const { i18n } = useTranslation('application');
  const { lang } = useParams();
  const currentLanguage = i18n.language;
  const prevLanguageRef = useRef(lang);
  const dispatch = useDispatch();
  const [page, setPage] = useLocalStorage('paginationCurrentPage', 1);
  const [currentPage, setCurrentPage] = useState(page);
  const appliedFilters = useSelector((state) => state.products.appliedFilters);
  const activeFilters = useSelector((state) => state.products.activeFilters);
  const mainCategory = useSelector((state) => state.products.mainCategory);
  const fetchedProductsData = useSelector((state) => state.products.fetchedProductsData);
  const prevFilters = useSelector((state) => state.products.parsedFilters);
  const prevPageRef = useRef(page);

  const shouldSkipQuery = useMemo(() => {
    const hasFetchedData = fetchedProductsData?.records?.length > 0;
    const filtersChanged = !_.isEqual(prevFilters, appliedFilters);
    const languageChanged = prevLanguageRef.current !== currentLanguage;
    const pageChanged = prevPageRef.current !== currentPage;

    if (filtersChanged) {
      dispatch(setParsedFilters(appliedFilters));
    }

    const shouldSkip = hasFetchedData && !filtersChanged && !languageChanged && !pageChanged;

    if (!shouldSkip) {
      prevLanguageRef.current = currentLanguage;
      prevPageRef.current = currentPage;
    }

    return shouldSkip;
  }, [appliedFilters, fetchedProductsData, currentLanguage, page, currentPage, prevPageRef]);

  const { isLoading } = useGetProductsQuery({ currentPage, filterParams: appliedFilters, lang: i18n.language }, { skip: shouldSkipQuery });
  const [currentProducts, setCurrentProducts] = useState(fetchedProductsData?.records || []);

  const productsref = useRef('');

  const parseActiveFilters = (filters, category) => {
    const allFilters = [];
    Object.keys(filters).forEach((filter) => {
      if (filters[filter].length !== 0) {
        if (filter === 'searchPhrase') {
          allFilters.push({ name: 'filters[q]', value: filters[filter] });
          return;
        }
        allFilters.push(...filters[filter]);
      }
    });
    if (category === 'Coffee') {
      allFilters.push({ name: 'filters[grouping]', value: 'coffee' });
    } else if (category === 'Tools') {
      allFilters.push({ name: 'filters[grouping]', value: 'equipment' });
    } else if (category === 'Archive') {
      allFilters.push({ name: 'filters[archived]', value: true });
    } else if (category === 'SpareParts') {
      allFilters.push({ name: 'filters[grouping]', value: 'spare_part' });
    }
    return allFilters;
  };

  useEffect(() => {
    const parsedFilters = parseActiveFilters(activeFilters, mainCategory);
    dispatch(setAppliedFilters([...parsedFilters]));
  }, [activeFilters, mainCategory]);

  useEffect(() => {
    if (!fetchedProductsData) {
      return;
    }

    setCurrentProducts(fetchedProductsData.records);

    if ((fetchedProductsData.records / PRODUCTS_PER_PAGE) < currentPage) {
      setCurrentPage(1);
    }
  }, [fetchedProductsData]);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    productsref.current.scrollIntoView({ behaviour: 'smooth', block: 'start' });
  };

  const returnProducts = () => {
    if (isLoading) {
      return (
        <Container className="single-product-container">
          <LoadingSpinner />
        </Container>
      );
    }
    if (currentProducts.length === 0) {
      return <NoSearchResultFound />;
    }
    return (
      <div className="grid-container" ref={productsref}>
        {currentProducts.map((product) => (
          <ProductItem key={`${i18n.language}-${product.id}`} product={product} />
        ))}
      </div>
    );
  };

  return (
    <>
      <AnimatePresence exitBeforeEnter>
        <motion.div key={currentPage}>{returnProducts()}</motion.div>
      </AnimatePresence>

      {currentProducts.length !== 0 && (
        <Pagination
          postsPerPage={PRODUCTS_PER_PAGE}
          totalPosts={fetchedProductsData?.total_count || 1}
          paginate={paginate}
          currentPage={currentPage}
        />
      )}
    </>
  );
};
